import React, { useState, useEffect, useRef } from 'react';
import { SqlFormatterService, SqlDialect, KeywordCase } from '../../lib/services/sqlFormatterService';
import { ClipboardUtils } from '../../lib/utils/clipboardUtils';
import { ErrorHandler } from '../../lib/utils/errorHandler';
import { EncoderToolType } from '../../lib/types';
import './SqlFormatter.css';

export const SqlFormatter: React.FC = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [mode, setMode] = useState<'format' | 'minify'>('format');
  const [dialect, setDialect] = useState<SqlDialect>('sql');
  const [indentSize, setIndentSize] = useState<number>(2);
  const [keywordCase, setKeywordCase] = useState<KeywordCase>('upper');
  const [copyFeedback, setCopyFeedback] = useState<'idle' | 'copied' | 'error'>('idle');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const copyButtonRef = useRef<HTMLButtonElement>(null);
  const processingRef = useRef<string>('');

  const handleFormat = async () => {
    if (!input.trim()) {
      setOutput('');
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    const processingKey = `${mode}-${dialect}-${indentSize}-${keywordCase}-${input.substring(0, 50)}`;
    processingRef.current = processingKey;

    try {
      let result;
      if (mode === 'format') {
        result = await SqlFormatterService.format(input, { dialect, indentSize, keywordCase });
      } else {
        result = await SqlFormatterService.minify(input);
      }

      if (processingRef.current === processingKey) {
        setOutput(result.outputData as string);
        setError(null);
      }
    } catch (err) {
      if (processingRef.current === processingKey) {
        const errorMessage = ErrorHandler.handle(err, EncoderToolType.SQL_FORMATTER);
        setError(errorMessage);
        setOutput('');
      }
    } finally {
      if (processingRef.current === processingKey) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (input) {
      const timer = setTimeout(() => {
        handleFormat();
      }, 300);

      return () => clearTimeout(timer);
    } else {
      setOutput('');
      setError(null);
      setIsLoading(false);
    }
  }, [input, mode, dialect, indentSize, keywordCase]);

  const handleCopy = async () => {
    if (!output) return;

    try {
      const success = await ClipboardUtils.copyText(output);
      if (success) {
        setCopyFeedback('copied');
        setTimeout(() => setCopyFeedback('idle'), 2000);
      } else {
        setCopyFeedback('error');
        setTimeout(() => setCopyFeedback('idle'), 2000);
      }
    } catch (error) {
      console.error('Copy error:', error);
      setCopyFeedback('error');
      setTimeout(() => setCopyFeedback('idle'), 2000);
    }
  };

  const getButtonText = () => {
    switch (copyFeedback) {
      case 'copied':
        return 'Copied!';
      case 'error':
        return 'Failed!';
      default:
        return 'Copy Result';
    }
  };

  const getButtonClassName = () => {
    const baseClass = 'copy-button';
    if (!output) return `${baseClass} copy-idle`;
    switch (copyFeedback) {
      case 'copied':
        return `${baseClass} copy-copied`;
      case 'error':
        return `${baseClass} copy-error`;
      default:
        return `${baseClass} copy-idle`;
    }
  };

  return (
    <div className="sql-formatter">
      <div style={{ marginBottom: '2rem' }}>
        <h1>SQL Formatter</h1>
        <p>
          Format or minify SQL queries. Supports multiple SQL dialects and customizable formatting options.
        </p>
      </div>

      <div className="encoder-container">
        <div className="form-group">
          <label>Mode:</label>
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value as 'format' | 'minify')}
            data-testid="mode-select"
          >
            <option value="format">Format</option>
            <option value="minify">Minify</option>
          </select>
        </div>

        {mode === 'format' && (
          <>
            <div className="form-group">
              <label>SQL Dialect:</label>
              <select
                value={dialect}
                onChange={(e) => setDialect(e.target.value as SqlDialect)}
                data-testid="dialect-select"
              >
                <option value="sql">Standard SQL</option>
                <option value="mysql">MySQL</option>
                <option value="postgresql">PostgreSQL</option>
                <option value="sqlite">SQLite</option>
              </select>
            </div>

            <div className="form-group">
              <label>Indentation:</label>
              <select
                value={indentSize}
                onChange={(e) => setIndentSize(Number(e.target.value))}
                data-testid="indent-select"
              >
                <option value={2}>2 spaces</option>
                <option value={4}>4 spaces</option>
              </select>
            </div>

            <div className="form-group">
              <label>Keyword Case:</label>
              <select
                value={keywordCase}
                onChange={(e) => setKeywordCase(e.target.value as KeywordCase)}
                data-testid="keyword-case-select"
              >
                <option value="upper">UPPERCASE</option>
                <option value="lower">lowercase</option>
                <option value="preserve">Preserve</option>
              </select>
            </div>
          </>
        )}

        <div className="form-group">
          <label>SQL Input:</label>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder='Enter SQL to format...'
            data-testid="input-text"
          />
        </div>

        {error && (
          <div className="status-message status-error">
            {error}
          </div>
        )}

        <div className="form-group">
          <label>{mode === 'format' ? 'Formatted SQL:' : 'Minified SQL:'}</label>
          <textarea
            value={output}
            readOnly
            placeholder={isLoading ? 'Processing...' : 'Result will appear here...'}
            className={isLoading ? 'loading' : ''}
            data-testid="output-text"
          />
          {isLoading && (
            <div className="loading-indicator">Processing...</div>
          )}
        </div>

        <div className="button-container">
          <button
            ref={copyButtonRef}
            onClick={handleCopy}
            disabled={!output}
            className={getButtonClassName()}
            data-testid="copy-button"
          >
            {getButtonText()}
          </button>
        </div>
      </div>
    </div>
  );
};
